import { JSX } from "react";
import { Helmet } from "react-helmet-async";
import logo from "@/assets/img/logo.png";

const styles = {
  logoContainer: "text-center mb-5",
  container: "container mx-auto py-10 px-6 lg:px-16",
  title: "text-4xl font-extrabold text-center text-gray-800 leading-tight",
  description: "mt-4 text-lg text-center text-gray-600 max-w-3xl mx-auto",
  section: "p-2 md:p-4",
  sectionTitle: "text-2xl font-semibold text-gray-800",
  sectionContent: "mt-4 text-gray-700",
  list: "mt-4 list-disc pl-6 text-gray-700",
  subsectionTitle: "text-xl font-semibold text-gray-800 mt-4",
  footer: "mt-10 text-center",
  footerText: "text-sm text-gray-600",
  logo: "h-auto w-full max-w-xs mx-auto",
};

const TermsAndConditionsPage = (): JSX.Element => {
  return (
    <main role="main" className={styles.container}>
      <Helmet>
        <title>Términos y Condiciones | AppCotas</title>
        <meta
          name="description"
          content="Consulta los términos y condiciones de uso de AppCotas. Conoce tus derechos y responsabilidades al utilizar nuestra plataforma."
        />
        <link rel="canonical" href="https://appcotas.ai/terms" />
      </Helmet>
      <header className={styles.logoContainer}>
        <a href={"/"} aria-label="Ir a la página principal">
          <img
            width={140}
            height={50}
            src={logo}
            alt="Logo AppCotas"
            className={styles.logo}
          />
        </a>
      </header>
      <h1 className={styles.title}>Términos y Condiciones</h1>
      <p className={styles.description}>
        A continuación se detallan los términos y condiciones para el uso de la
        aplicación AppCotas.
      </p>
      <article>
        <section className={styles.section} aria-labelledby="intro">
          <h2 id="intro" className={styles.sectionTitle}>
            1. Introducción
          </h2>
          <p className={styles.sectionContent}>
            AppCotas es una aplicación digital diseñada para facilitar la
            gestión de la salud de mascotas, incluyendo la búsqueda de
            profesionales veterinarios, historial médico de las mascotas,
            programación de turnos, acceso a servicios veterinarios y más. Al
            usar AppCotas, el Usuario (usted) acepta los términos y condiciones
            que se describen a continuación.
          </p>
        </section>
        <section className={styles.section} aria-labelledby="general">
          <h2 id="general" className={styles.sectionTitle}>
            2. General
          </h2>
          <p className={styles.sectionContent}>
            Estos Términos y Condiciones regulan la relación entre AppCotas y
            usted como Usuario de la aplicación, ya sea en su versión web o
            móvil. Al instalar, acceder, navegar o usar la aplicación, el
            Usuario reconoce haber leído, entendido y aceptado cumplir con estos
            términos.
          </p>
        </section>
        <section className={styles.section} aria-labelledby="desc-app">
          <h2 id="desc-app" className={styles.sectionTitle}>
            3. Descripción de la Aplicación
          </h2>
          <p className={styles.sectionContent}>
            AppCotas ofrece las siguientes funcionalidades:
          </p>
          <ul className={styles.list}>
            <li>Registro y seguimiento médico de mascotas.</li>
            <li>Búsqueda de veterinarios y servicios.</li>
            <li>Gestión de citas y consultas.</li>
            <li>Alertas de salud y recordatorios.</li>
            <li>Interacción con profesionales veterinarios.</li>
            <li>
              Acceso a servicios adicionales como adopciones y tienda de
              productos.
            </li>
          </ul>
        </section>
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>4. Uso de la Aplicación</h3>
          <h4 className={styles.subsectionTitle}>
            Requisitos para el Usuario:
          </h4>
          <ul className={styles.list}>
            <li>
              Ser mayor de 18 años y contar con capacidad legal para contratar.
            </li>
            <li>
              Proporcionar información precisa y actualizada al registrarse en
              la aplicación.
            </li>
            <li>
              Usar la aplicación únicamente para fines legales relacionados con
              el cuidado de mascotas.
            </li>
          </ul>
          <h4 className={styles.subsectionTitle}>Prohibiciones:</h4>
          <ul className={styles.list}>
            <li>
              Copiar, modificar o descompilar cualquier parte del contenido o
              del código de la aplicación.
            </li>
            <li>
              Usar la aplicación para fines ilegales, inmorales o que infrinjan
              derechos de terceros.
            </li>
          </ul>
        </section>
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>
            5. Privacidad y Protección de Datos Personales
          </h3>
          <p className={styles.sectionContent}>
            AppCotas recopila y utiliza los datos personales del Usuario, así
            como la información médica de las mascotas, únicamente para los
            fines previstos en la aplicación, como la gestión de turnos,
            historial médico y seguimiento de planes de salud. El Usuario
            autoriza a AppCotas a procesar esta información, garantizando la
            protección conforme a la Ley de Protección de Datos Personales
            vigente.
          </p>
        </section>
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>
            6. Responsabilidades del Usuario
          </h3>
          <ul className={styles.list}>
            <li>Proporcionar información veraz sobre sus mascotas.</li>
            <li>Mantener la confidencialidad de sus credenciales de acceso.</li>
            <li>
              No usar AppCotas como sustituto exclusivo de la atención
              veterinaria presencial en casos de emergencia.
            </li>
          </ul>
        </section>
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>
            7. Relación con Veterinarios y Clínicas
          </h3>
          <p className={styles.sectionContent}>
            AppCotas actúa como intermediario entre el Usuario y los
            veterinarios o clínicas veterinarias. No interviene en las
            decisiones profesionales ni garantiza la disponibilidad o calidad de
            los servicios ofrecidos por los profesionales.
          </p>
        </section>
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>8. Licencia de Uso</h3>
          <p className={styles.sectionContent}>
            AppCotas otorga al Usuario una licencia limitada, personal, no
            transferible y no exclusiva para el uso de la aplicación, conforme a
            estos Términos y Condiciones.
          </p>
        </section>
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>9. Propiedad Intelectual</h3>
          <p className={styles.sectionContent}>
            Todo el contenido de AppCotas, incluyendo logotipos, diseños,
            imágenes y software, es propiedad exclusiva de AppCotas. El Usuario
            no podrá copiar, modificar, distribuir ni utilizar este contenido
            sin autorización previa.
          </p>
        </section>
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>
            10. Notificaciones y Comunicaciones
          </h3>
          <p className={styles.sectionContent}>
            AppCotas podrá enviar notificaciones dentro de la aplicación para
            informar al Usuario sobre actualizaciones, recordatorios y cambios
            en estos Términos y Condiciones.
          </p>
        </section>
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>
            11. Modificaciones de los Términos y Condiciones
          </h3>
          <p className={styles.sectionContent}>
            AppCotas se reserva el derecho de modificar estos Términos y
            Condiciones en cualquier momento. Es responsabilidad del Usuario
            revisar periódicamente las actualizaciones publicadas en la
            aplicación.
          </p>
        </section>
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>
            12. Legislación Aplicable y Jurisdicción
          </h3>
          <p className={styles.sectionContent}>
            Estos Términos y Condiciones se rigen por la legislación vigente en
            la República Argentina. Cualquier conflicto se resolverá en los
            Tribunales de la Ciudad Autónoma de Buenos Aires.
          </p>
        </section>
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>13. Cancelación de Cuentas</h3>
          <p className={styles.sectionContent}>
            El Usuario puede cancelar su cuenta en cualquier momento a través de
            la configuración de la aplicación. AppCotas se reserva el derecho de
            suspender o cancelar cuentas que violen estos Términos y Condiciones
            o que se utilicen para actividades fraudulentas o ilegales.
          </p>
        </section>
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>
            14. Limitación de Responsabilidad
          </h3>
          <p className={styles.sectionContent}>
            AppCotas no se hace responsable por daños directos, indirectos,
            incidentales, especiales o consecuentes que resulten del uso o la
            imposibilidad de usar la aplicación, incluyendo, entre otros,
            pérdida de datos, interrupciones del servicio o errores en la
            información proporcionada por terceros.
          </p>
        </section>
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>15. Soporte Técnico</h3>
          <p className={styles.sectionContent}>
            AppCotas ofrece soporte técnico a través de los canales oficiales
            indicados en la aplicación. El tiempo de respuesta puede variar
            según la complejidad del problema y la disponibilidad del equipo de
            soporte.
          </p>
        </section>
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>16. Uso Internacional</h3>
          <p className={styles.sectionContent}>
            Aunque AppCotas está disponible en varios países, no garantizamos
            que la aplicación sea adecuada o esté disponible para su uso en
            todas las ubicaciones. Los Usuarios son responsables de cumplir con
            las leyes locales aplicables al usar la aplicación.
          </p>
        </section>
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>17. Fuerza Mayor</h3>
          <p className={styles.sectionContent}>
            AppCotas no será responsable por el incumplimiento o retraso en el
            cumplimiento de sus obligaciones debido a eventos fuera de su
            control razonable, incluyendo, entre otros, desastres naturales,
            interrupciones en los servicios de telecomunicaciones, pandemias,
            conflictos laborales o actos gubernamentales.
          </p>
        </section>
        <section className={styles.footer} aria-label="Pie de página">
          <p className={styles.footerText}>
            Si tiene alguna duda sobre estos términos, no dude en ponerse en
            contacto con nosotros.
          </p>
        </section>
      </article>
    </main>
  );
};

export default TermsAndConditionsPage;
